import type { PhotoInspiration } from '../types.js';

const ph = (city: string, slug: string) => `/inspo/${city}/${slug}.jpg`;
// images fall back to the placeholder until real photos are dropped into public/inspo/<city>/

export const INSPIRATION: PhotoInspiration[] = [
	// Seoul
	{
		id: 'seoul-gyeongbokgung',
		city: 'seoul',
		title: 'Hanbok under the palace eaves',
		spot: 'Gyeongbokgung Palace',
		tip: 'Frame a hanbok-clad subject beneath Geunjeongjeon’s eaves; the 10:00 guard ceremony adds colour and motion.',
		bestTime: 'Morning (10:00 ceremony)',
		image: ph('seoul', 'gyeongbokgung'),
	},
	{
		id: 'seoul-bukchon',
		title: 'Bukchon alley lines',
		city: 'seoul',
		spot: 'Bukchon-ro 11-gil',
		tip: 'Shoot down the sloping alley so the hanok rooflines converge toward the city below. Go early — fewer crowds, softer light.',
		bestTime: 'Early morning',
		image: ph('seoul', 'bukchon'),
	},
	{
		id: 'seoul-banpo',
		title: 'Rainbow fountain long-exposure',
		city: 'seoul',
		spot: 'Banpo Hangang Park',
		tip: 'Brace on a railing or mini-tripod for a 1–2 s exposure to streak the water. Arrive before the 20:00 show for a spot.',
		bestTime: 'Blue hour, 20:00',
		image: ph('seoul', 'banpo'),
	},
	{
		id: 'seoul-nseoul',
		title: 'Skyline from Namsan',
		city: 'seoul',
		spot: 'N Seoul Tower',
		tip: 'Use the love-locks as foreground and let the city lights spread behind at blue hour.',
		bestTime: 'Blue hour',
		image: ph('seoul', 'nseoul'),
	},
	{
		id: 'seoul-gwangjang',
		title: 'Market steam & motion',
		city: 'seoul',
		spot: 'Gwangjang Market',
		tip: 'Go in tight on a stall — fast shutter to freeze the steam and a vendor’s hands. Candid beats posed here.',
		bestTime: 'Evening',
		image: ph('seoul', 'gwangjang'),
	},
	{
		id: 'seoul-cheonggyecheon',
		title: 'Stream reflections',
		city: 'seoul',
		spot: 'Cheonggyecheon',
		tip: 'Drop low to catch city lights mirrored on the water; people on stepping stones add scale.',
		bestTime: 'Night',
		image: ph('seoul', 'cheonggyecheon'),
	},

	// Jeju
	{
		id: 'jeju-seongsan',
		title: 'Sunrise Peak from the coast',
		city: 'jeju',
		spot: 'Seongsan Ilchulbong',
		tip: 'Shoot the crater wide from the coastal path at first light; include a haenyeo or fishing boat for scale.',
		bestTime: 'Sunrise',
		image: ph('jeju', 'seongsan'),
	},
	{
		id: 'jeju-udo',
		title: 'Turquoise at Seobinbaeksa',
		city: 'jeju',
		spot: 'Udo Island',
		tip: 'A polariser deepens the turquoise water against the white coral sand. Midday sun actually helps here.',
		bestTime: 'Midday',
		image: ph('jeju', 'udo'),
	},
	{
		id: 'jeju-manjanggul',
		title: 'Lava-tube textures',
		city: 'jeju',
		spot: 'Manjanggul Lava Tube',
		tip: 'It’s dark — brace against the rail for a long exposure, or push ISO. The lava column at the end is the hero shot.',
		bestTime: 'Anytime (indoor)',
		image: ph('jeju', 'manjanggul'),
	},
	{
		id: 'jeju-jusangjeolli',
		title: 'Basalt columns & swell',
		city: 'jeju',
		spot: 'Jusangjeolli Cliffs',
		tip: 'Time the waves crashing into the hexagonal columns; a slightly slow shutter softens the spray.',
		bestTime: 'Afternoon',
		image: ph('jeju', 'jusangjeolli'),
	},
	{
		id: 'jeju-harubang',
		title: 'Harubang & green fields',
		city: 'jeju',
		spot: 'Seongeup / roadside',
		tip: 'Frame a stone grandfather (dol hareubang) against Jeju’s green volcanic fields and low stone walls.',
		bestTime: 'Golden hour',
		image: ph('jeju', 'harubang'),
	},

	// Busan
	{
		id: 'busan-gamcheon',
		title: 'Pastel rooftops layered',
		city: 'busan',
		spot: 'Gamcheon Culture Village',
		tip: 'From the main viewpoint, compress the layered pastel houses with a longer focal length. Mid-afternoon light is even.',
		bestTime: 'Afternoon',
		image: ph('busan', 'gamcheon'),
	},
	{
		id: 'busan-yonggungsa',
		title: 'Temple on the rocks',
		city: 'busan',
		spot: 'Haedong Yonggungsa',
		tip: 'Use morning side-light; catch surf breaking below the temple. The stone pagoda makes a clean foreground.',
		bestTime: 'Morning',
		image: ph('busan', 'yonggungsa'),
	},
	{
		id: 'busan-skycapsule',
		title: 'Pastel capsule & sea',
		city: 'busan',
		spot: 'Haeundae Blueline Park',
		tip: 'Shoot through the capsule window with the sea as backdrop, or catch a capsule passing on the elevated track.',
		bestTime: 'Daytime',
		image: ph('busan', 'skycapsule'),
	},
	{
		id: 'busan-gwangalli',
		title: 'Gwangan Bridge blue hour',
		city: 'busan',
		spot: 'Gwangalli Beach',
		tip: 'From the sand, lit bridge across the bay at blue hour; a long exposure smooths the water.',
		bestTime: 'Blue hour',
		image: ph('busan', 'gwangalli'),
	},
	{
		id: 'busan-huinnyeoul',
		title: 'White seaside alleys',
		city: 'busan',
		spot: 'Huinnyeoul Culture Village',
		tip: 'Narrow whitewashed lanes opening to the sea — wait for a single figure to walk into the gap.',
		bestTime: 'Late afternoon',
		image: ph('busan', 'huinnyeoul'),
	},
	{
		id: 'busan-bay101',
		title: 'Marine City reflections',
		city: 'busan',
		spot: 'The Bay 101',
		tip: 'After dark, the skyline reflects on the wet pier — get low and use the puddles as a mirror.',
		bestTime: 'Night',
		image: ph('busan', 'bay101'),
	},
];
